/**
 * PWA file handler.
 *
 * When the installed app is launched with a `.lakar` file, the OS hands a handle over through
 * `launchQueue`. The project is read once and imported into the editor; saving then follows the
 * normal storage rules (browser OPFS, or the linked PC folder when one is open), so no
 * half-remembered file handle has to stay valid between sessions. Export ▸ `.lakar` writes the
 * file back out when the user wants it on disk.
 */

/** What the OS hands over when a `.lakar` file is opened with the installed app. */
export interface LaunchFileHandle {
  readonly name: string;
  getFile(): Promise<File>;
}

export interface LaunchParamsLike {
  files?: LaunchFileHandle[];
}

interface LaunchQueueHost {
  launchQueue?: { setConsumer(consumer: (params: LaunchParamsLike) => void): void };
}

export interface ProjectOpener {
  open(text: string, name: string): void;
}

/** No-ops in browsers without the File Handling API (`launchQueue`). */
export function installFileLaunch(opener: ProjectOpener, host: Window = window): boolean {
  const queue = (host as unknown as LaunchQueueHost).launchQueue;
  if (!queue) return false;
  queue.setConsumer((params) => {
    void openLaunched(opener, params);
  });
  return true;
}

export async function openLaunched(
  opener: ProjectOpener,
  params: LaunchParamsLike,
): Promise<void> {
  const handle = params.files?.[0];
  if (!handle) return;
  const file = await handle.getFile();
  opener.open(await file.text(), file.name || handle.name);
}
